import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowUpIcon,
  BlueprintIcon,
  BookBookmarkIcon,
  CaretDownIcon,
  CubeIcon,
  GitBranchIcon,
  HexagonIcon,
  RobotIcon,
} from '@phosphor-icons/react';
import toast from 'react-hot-toast';

import { createAgentSession, type AgentSessionMode } from '@/api-models/agent-sessions';
import { useModels } from '@/api-models/models';
import { useRepositories, type Repository } from '@/api-models/repositories';
import { useBranches } from '@/api-models/workspaces';
import { teamPath, useSpace } from '@/contexts/space-context';
import { LocationBar } from '@/shared/shell/LocationBar';
import { Button } from '@/ui/button/Button';
import { SegmentedControl } from '@/ui/SegmentedControl';
import { Textarea } from '@/ui/Textarea';

import { ComposerMenu } from '../components/ComposerMenu';
import { ImageThumbnails } from '../components/ImageAttachments';
import { DropdownPanel, DropdownRow } from '../components/RepoBranchPickers';
import { useImageAttachments } from '../hooks/useImageAttachments';

const repoLabel = (p: Repository) => p.repo_full_name ?? p.name;

/**
 * Chat-first "New session" entrypoint. Write a first prompt, pick a repository +
 * branch, then spin up a session backed by a fresh Daytona sandbox.
 */
export function NewSessionPage() {
  const { teamId } = useSpace();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { repositories: projects, isLoading: projectsLoading } = useRepositories(teamId);
  const { models, defaultModel } = useModels();

  const [prompt, setPrompt] = useState(() => searchParams.get('prompt') ?? '');
  const [projectId, setProjectId] = useState(() => searchParams.get('project') ?? '');
  // The base branch the session branches off from; the server always creates a
  // fresh `zooid-…` working branch on top of it.
  const [branch, setBranch] = useState(() => searchParams.get('branch') ?? '');
  const [model, setModel] = useState('');
  // Plan mode provisions no sandbox: the agent reads the repository read-only and drafts a plan,
  // and a sandbox is created only if the user later starts coding from it.
  const [mode, setMode] = useState<AgentSessionMode>('plan');
  const [repoOpen, setRepoOpen] = useState(false);
  const [branchOpen, setBranchOpen] = useState(false);
  const [repoQuery, setRepoQuery] = useState('');
  const [branchQuery, setBranchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // The model picked here is the one the session will run on, so it decides
  // whether images can be attached at all.
  const attachments = useImageAttachments(model || null);

  const selectedProject = projects.find((p) => p.id === projectId) ?? null;
  const { branches, isLoading: branchesLoading } = useBranches(projectId || undefined, !!projectId);

  const prevProjectRef = useRef(projectId);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Default the repository to the first project once they load.
  useEffect(() => {
    const first = projects[0];
    if (!projectId && first) setProjectId(first.id);
  }, [projectId, projects]);

  // When the repo changes (a real switch, not the initial seed), reset the
  // branch to the new repo's default.
  useEffect(() => {
    if (prevProjectRef.current === projectId) return;
    prevProjectRef.current = projectId;
    const proj = projects.find((p) => p.id === projectId);
    setBranch(proj?.default_branch ?? '');
  }, [projectId, projects]);

  // Once branches load, fall back to the default branch if none is chosen yet.
  useEffect(() => {
    if (branch || !branches.length) return;
    const def = branches.find((b) => b.is_default) ?? branches[0];
    if (def) setBranch(def.name);
  }, [branch, branches]);

  // Default the model to the list's default (or first) once models load.
  useEffect(() => {
    if (model) return;
    const first = models[0];
    if (!first) return;
    setModel(defaultModel ?? first.id);
  }, [model, models, defaultModel]);

  // Auto-grow the composer textarea to fit its content.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 320)}px`;
  }, [prompt]);

  async function handleStart() {
    if (!projectId || submitting) return;
    const invalid = attachments.validate();
    if (invalid) {
      toast.error(invalid);
      return;
    }
    setSubmitting(true);
    try {
      const session = await createAgentSession({
        repository_id: projectId,
        prompt: prompt.trim() || undefined,
        branch: branch || undefined,
        model: model || undefined,
        images: attachments.images.length > 0 ? attachments.images : undefined,
        mode,
      });
      navigate(teamPath(teamId, mode === 'plan' ? `/sessions/${session.id}/plan` : `/sessions/${session.id}`));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : `Failed to start ${mode === 'plan' ? 'plan' : 'session'}`);
      setSubmitting(false);
    }
  }

  const filteredProjects = projects.filter((p) => repoLabel(p).toLowerCase().includes(repoQuery.trim().toLowerCase()));
  const filteredBranches = branches.filter((b) => b.name.toLowerCase().includes(branchQuery.trim().toLowerCase()));

  return (
    <div className="flex h-full flex-col leading-tight">
      <LocationBar>
        <RobotIcon size={16} weight="bold" className="shrink-0 text-brand-text" />
        <span className="text-sm font-semibold text-fg">New session</span>
      </LocationBar>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto flex w-full max-w-2xl flex-col items-center px-6 pb-16 pt-[12vh]">
          {/* Brand mark + wordmark */}
          <div className="flex items-center gap-2.5">
            <span className="flex size-9 items-center justify-center rounded-md bg-brand">
              <HexagonIcon size={20} weight="bold" className="text-white" />
            </span>
            <span className="font-mono text-xl font-bold tracking-tight text-fg">zooid</span>
          </div>
          <h1 className="mt-5 text-xl font-semibold text-fg">What should we work on?</h1>
          <p className="mt-1.5 text-sm text-fg-secondary">
            {mode === 'plan'
              ? 'Think a change through first — the agent reads the repository and drafts a plan.'
              : "Describe a task in plain language and zooid's agent gets to work."}
          </p>

          <div className="mt-5">
            <SegmentedControl
              value={mode}
              onChange={setMode}
              ariaLabel="Session mode"
              options={[
                {
                  value: 'plan',
                  label: (
                    <span className="flex items-center gap-1.5">
                      <BlueprintIcon size={14} className="shrink-0" />
                      Plan
                    </span>
                  ),
                },
                {
                  value: 'code',
                  label: (
                    <span className="flex items-center gap-1.5">
                      <CubeIcon size={14} className="shrink-0" />
                      Build
                    </span>
                  ),
                },
              ]}
            />
          </div>

          {/* Composer */}
          <div className="mt-7 w-full">
            <div
              className={`flex flex-col gap-2 rounded-xl border bg-surface-2 p-2 transition-colors focus-within:border-line-strong ${
                attachments.dragging && attachments.canAttach ? 'border-brand-border' : 'border-line'
              }`}
              {...attachments.dropZoneProps}
            >
              <ImageThumbnails images={attachments.images} onRemove={attachments.remove} />
              <div className="flex items-end gap-2">
                <ComposerMenu
                  inputRef={attachments.fileInputRef}
                  onFileInputChange={attachments.onFileInputChange}
                  canAttach={attachments.canAttach}
                  model={model || null}
                  onSelectModel={setModel}
                />
                <Textarea
                  ref={textareaRef}
                  autoFocus
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  onPaste={attachments.onPaste}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      void handleStart();
                    }
                  }}
                  placeholder={
                    attachments.canAttach
                      ? 'What should the agent work on? Paste or drop images to attach them.'
                      : 'What should the agent work on?'
                  }
                  rows={2}
                  className="max-h-80 border-0 bg-transparent px-2 py-1.5 focus:ring-0"
                />
                <Button
                  variant="primary"
                  shape="square"
                  size={9}
                  onClick={() => void handleStart()}
                  disabled={!projectId || submitting}
                  isLoading={submitting}
                  aria-label="Start session"
                >
                  {!submitting && <ArrowUpIcon size={16} weight="bold" />}
                </Button>
              </div>

              {/* Toolbar: repository + branch pickers */}
              <div className="flex flex-wrap items-center gap-2 px-1">
                <div className="relative">
                  <PickerPill
                    icon={BookBookmarkIcon}
                    label={
                      selectedProject
                        ? repoLabel(selectedProject)
                        : projectsLoading
                          ? 'Loading repositories…'
                          : 'No repositories'
                    }
                    placeholder={!selectedProject}
                    onClick={() => {
                      setRepoOpen((v) => !v);
                      setBranchOpen(false);
                    }}
                  />
                  {repoOpen && (
                    <div className="absolute left-0 top-full z-20 mt-1 w-72 max-w-[calc(100vw-3rem)]">
                      <DropdownPanel
                        query={repoQuery}
                        onQuery={setRepoQuery}
                        placeholder="Search repositories"
                        empty={projectsLoading ? 'Loading repositories…' : 'No repositories found'}
                        isEmpty={filteredProjects.length === 0}
                      >
                        {filteredProjects.map((p) => (
                          <DropdownRow
                            key={p.id}
                            icon={BookBookmarkIcon}
                            label={repoLabel(p)}
                            selected={p.id === projectId}
                            onClick={() => {
                              setProjectId(p.id);
                              setRepoOpen(false);
                              setRepoQuery('');
                            }}
                          />
                        ))}
                      </DropdownPanel>
                    </div>
                  )}
                </div>

                <div className="relative">
                  <PickerPill
                    icon={GitBranchIcon}
                    label={branch || (branchesLoading ? 'Loading branches…' : 'Select a base branch')}
                    placeholder={!branch}
                    disabled={!projectId}
                    onClick={() => {
                      setBranchOpen((v) => !v);
                      setRepoOpen(false);
                    }}
                  />
                  {branchOpen && (
                    <div className="absolute left-0 top-full z-20 mt-1 w-72 max-w-[calc(100vw-3rem)]">
                      <DropdownPanel
                        query={branchQuery}
                        onQuery={setBranchQuery}
                        placeholder="Search branches"
                        empty={branchesLoading ? 'Loading branches…' : 'No branches found'}
                        isEmpty={filteredBranches.length === 0}
                      >
                        {filteredBranches.map((b) => (
                          <DropdownRow
                            key={b.name}
                            icon={GitBranchIcon}
                            label={b.name}
                            selected={b.name === branch}
                            onClick={() => {
                              setBranch(b.name);
                              setBranchOpen(false);
                              setBranchQuery('');
                            }}
                          />
                        ))}
                      </DropdownPanel>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <span className="mt-2.5 flex items-center justify-center gap-1.5 text-fg-tertiary">
              {mode === 'plan' ? (
                <>
                  <BlueprintIcon size={14} className="shrink-0" />
                  <span className="text-xs">Read-only — no sandbox until you start coding</span>
                </>
              ) : (
                <>
                  <CubeIcon size={14} className="shrink-0" />
                  <span className="text-xs">Runs in an isolated sandbox</span>
                </>
              )}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Compact pill-style trigger for a repo/branch picker in the composer toolbar. */
function PickerPill({
  icon: IconCmp,
  label,
  placeholder,
  disabled,
  onClick,
}: {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  label: string;
  placeholder?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="flex max-w-64 items-center gap-1.5 rounded-md px-2 py-1.5 text-fg-secondary transition-colors hover:bg-surface-3 hover:text-fg disabled:opacity-50 disabled:hover:bg-transparent"
    >
      <IconCmp size={14} className="shrink-0" />
      <span className={`truncate font-mono text-xs ${placeholder ? 'text-fg-tertiary' : 'text-fg'}`}>{label}</span>
      <CaretDownIcon size={13} className="shrink-0 text-fg-tertiary" />
    </button>
  );
}
